/**
 * @file Desktop Navigation component is rendered on medium and large devices.
 * @TODO integrate with react router, links currently lead to nowhere.
 *
 */
import React, { Component, Fragment } from 'react';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { Grow, Paper, Popper, MenuItem, MenuList, Grid } from '@mui/material';
import { Launch, ExpandMore } from '@mui/icons-material';

import { NavLink } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { menuNavigationProps } from '../../../types/centralizedProps';
import Logo from '../../../assets/logo/locumtenens_com_logo.svg';
import { COLORS } from '../../../utils/Application_Constants';
import ProfileNavComponent from '../../ContentComponents/ProfileNavigation/ProfileNavComponent';
import NavigationService from '../../../services/NavigationService';
import UserContext from '../../../contexts/userContext';
import Notifications from '../Notifications/Notifications';

const styles = () => ({
    menuListRoot: {
        display: 'flex',
    },
    logoWrapper: {
        maxWidth: '270px',
    },
    supportNumberMenuListRoot: {
        display: 'flex',
        paddingLeft: '5px',
        fontSize: '14px',
    },
    buttonWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '10px 0px 18px 0px',
        marginTop: '15px',
        flexGrow: '1',
    },
    navbarWrapper: {
        padding: '0 15px',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        color: `${COLORS.DENIM}`,
        position: 'absolute',
    },
    fontBoldHeader: {
        fontWeight: '700',
        fontSize: '14px',
        padding: '12px 10px',
        transition: '0.3s all',
        '&:hover': {
            background: 'none',
        },
    },

    paperPopper: {
        zIndex: '1302',
    },
    subNavRoot: {
        padding: '32px 49px 30px 30px',
    },
    gridFlexWrapper: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    supportNavNumber: {
        fontWeight: '700',
        color: COLORS.TRUE_BLUE,
        fontSize: '14px',
        cursor: 'pointer',
        padding: '12px 10px',
        transition: '0.3s all',
        '&:hover': {
            background: 'none',
            color: COLORS.DARK_BLUE,
        },
    },
    subNavMenuItem: {
        fontWeight: '700',
        color: `${COLORS.TRUE_BLUE}`,
        fontSize: '14px',
        '&:hover': {
            background: COLORS.TRUE_BLUE,
            color: '#ffffff',
        },
    },
    deskLink: {
        textTransform: 'upperCase',
        borderBottom: '2px solid #ffffff',
        transition: '0.3s all',
        '&:hover': {
            color: COLORS.DARK_BLUE,
            borderBottom: `2px solid ${COLORS.DARK_BLUE}`,
            background: 'none',
        },
        '&.active': {
            borderBottom: `1px solid ${COLORS.DENIM}`,
            transition: '0.3s all',
        },
        position: 'relative',
        zIndex: '1303',
    },
    arrowIcon: {
        marginLeft: '6px',
    },
});

class DesktopNavigation extends Component {
    constructor(props, context) {
        super(props);
        this.state = {
            childMenuOpen: null,
            profileNavigation: [],
            isAdminApp: process.env.REACT_APP_TYPE === 'Admin',
            isClientApp: process.env.REACT_APP_TYPE === 'Client',
        };
        this.anchorRef = React.createRef(null);
        this.context = context;
    }

    componentDidMount() {
        const profileNavigation = NavigationService.getProfileNavigation();
        this.setState({ profileNavigation: profileNavigation.profile });
    }

    renderSupportNumber = () => {
        const { t, classes } = this.props;
        return (
            <a href={t('common:INFORMATION.HELP_NUMBER_LINK')} className={classes.supportNavNumber}>
                {' '}
                {t('common:INFORMATION.HELP_NUMBER')}
            </a>
        );
    };

    /**
     * Method renders a single item as part of navigation.
     * @param {any} navigationItem
     * @returns {JSX}
     */
    renderNavigationItem = (navigationItem) => {
        const { classes } = this.props;
        const { id, name, parentUrlLink } = navigationItem;

        return (
            <NavLink
                exact
                key={id.toString()}
                to={{ pathname: parentUrlLink }}
                className={classes.deskLink}
            >
                <MenuItem className={classes.fontBoldHeader}>{name}</MenuItem>
            </NavLink>
        );
    };

    /**
     * Main navigation method called in the render method. This method decides
     * how to render the navigation based on subnav properties.
     */
    renderNavigation = () => {
        const { menuNavigation } = this.props;
        return menuNavigation.map((item) => {
            if (item.children.length > 0) {
                return this.renderNavItemWithSubMenu(item);
            }
            return this.renderNavigationItem(item);
        });
    };

    handleNavItemWithSubMenuClick = (event) => {
        const { childMenuOpen } = this.state;
        let dropdownSelected = event.target.innerText;

        if (dropdownSelected) {
            this.anchorRef = event.target;
        } else {
            // if a child element of the menu item is clicked, we need to find the parent menu item itself to ensure it's still clickable
            const parentMenuItem = event.target.closest('[role="menuitem"]');
            dropdownSelected = parentMenuItem.innerText;
            this.anchorRef = parentMenuItem;
        }

        if (childMenuOpen) {
            this.setState({ childMenuOpen: null });
        } else {
            this.setState({ childMenuOpen: dropdownSelected });
        }
    };

    renderNavItemWithSubMenu(navigationItem) {
        const { classes } = this.props;
        const { id, name, isParentClickable, parentUrlLink } = navigationItem;
        return (
            <Fragment key={`frag-${id.toString()}`}>
                <NavLink
                    to={{ pathname: parentUrlLink }}
                    onClick={(event) => {
                        // if isParentClickable is marked as false, we don't want to navigate to the page
                        // we need parentUrlLink to still be set on the parent to ensure the navlink is underlined
                        if (!isParentClickable) {
                            event.preventDefault();
                        }
                    }}
                    className={classes.deskLink}
                >
                    <MenuItem
                        className={classes.fontBoldHeader}
                        onClick={this.handleNavItemWithSubMenuClick}
                        onClose={(event) => {
                            this.anchorRef = event.target;
                        }}
                        onBlur={(event) => {
                            this.anchorRef = event.target;
                            this.setState({ childMenuOpen: null });
                        }}
                    >
                        {name}
                        <ExpandMore className={classes.arrowIcon} fontSize="small" />
                    </MenuItem>
                </NavLink>
                <Popper
                    open={
                        this.state.childMenuOpen?.toUpperCase().trim() ===
                        navigationItem.name?.toUpperCase().trim()
                    }
                    anchorEl={this.anchorRef}
                    transition
                    className={classes.paperPopper}
                    placement="bottom-start"
                    modifiers={[
                        {
                            enabled: true,
                            name: 'offset',
                            options: {
                                offset: [0, 2],
                            },
                        },
                    ]}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin:
                                    placement === 'bottom' ? 'center top' : 'center bottom',
                            }}
                        >
                            <Paper>
                                <MenuList>
                                    {navigationItem.children.map((item, index) => {
                                        if (item.externalUrl) {
                                            return (
                                                <a
                                                    key={`a-${item.id.toString()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={item.link}
                                                    className={classes.deskLink}
                                                >
                                                    <MenuItem
                                                        classes={{ root: classes.subNavMenuItem }}
                                                    >
                                                        {item.name}
                                                        <Launch
                                                            fontSize="small"
                                                            style={{ marginLeft: 'auto' }}
                                                        />
                                                    </MenuItem>
                                                </a>
                                            );
                                        }
                                        if (item.hasLtDotComBaseUrl) {
                                            return (
                                                <a
                                                    key={`a-${item.id.toString()}`}
                                                    href={`${process.env.REACT_APP_LOGIN_URL}${item.link}`}
                                                    className={classes.deskLink}
                                                >
                                                    <MenuItem
                                                        classes={{ root: classes.subNavMenuItem }}
                                                    >
                                                        {item.name}
                                                    </MenuItem>
                                                </a>
                                            );
                                        }
                                        return (
                                            <NavLink
                                                key={`NavLink-${item.id.toString()}`}
                                                exact
                                                to={{ pathname: item.link }}
                                                className={classes.deskLink}
                                            >
                                                <MenuItem
                                                    classes={{ root: classes.subNavMenuItem }}
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            </NavLink>
                                        );
                                    })}
                                </MenuList>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Fragment>
        );
    }

    render() {
        const { classes, refreshNotifications } = this.props;
        const { profileNavigation, isAdminApp, isClientApp } = this.state;
        const { account } = this.context;

        return (
            <Grid
                container
                justifyContent="flex-start"
                alignContent="center"
                className={classes.navbarWrapper}
            >
                <Grid item classes={{ root: classes.logoWrapper }} md={3}>
                    <a href="/">
                        <img alt="LocumTenens Logo" src={Logo} />
                    </a>
                </Grid>
                <Grid item md={6}>
                    <MenuList classes={{ root: classes.menuListRoot }}>
                        {this.renderNavigation()}
                    </MenuList>
                </Grid>

                <Grid item md="auto" classes={{ root: classes.buttonWrapper }}>
                    <MenuList classes={{ root: classes.supportNumberMenuListRoot }}>
                        {this.renderSupportNumber()}
                    </MenuList>

                    {!isClientApp && !isAdminApp && (
                        <span>
                            <MenuList>
                                <Notifications
                                    refreshNotifications={refreshNotifications}
                                    device="desktop"
                                />
                            </MenuList>
                        </span>
                    )}

                    <span>
                        <ProfileNavComponent profileNavigation={account ? profileNavigation : []} />
                    </span>
                </Grid>
            </Grid>
        );
    }
}

DesktopNavigation.propTypes = {
    classes: PropTypes.shape({
        menuListRoot: PropTypes.string.isRequired,
        logoWrapper: PropTypes.string.isRequired,
        navbarWrapper: PropTypes.string.isRequired,
        fontBoldHeader: PropTypes.string.isRequired,
        paperPopper: PropTypes.string.isRequired,
        subNavRoot: PropTypes.string.isRequired,
        gridFlexWrapper: PropTypes.string.isRequired,
    }).isRequired,
    menuNavigation: menuNavigationProps.isRequired,
};

DesktopNavigation.contextType = UserContext;
export default withTranslation()(withStyles(styles)(DesktopNavigation));
